import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listProducts, type Product } from './api';

/** Catalog filters a caller may pin; `search`/`page`/`pageSize` are owned here. */
export type ProductSearchFilter = {
  activeOnly?: boolean;
  componentEligible?: boolean;
  componentAllowBundles?: boolean;
  virtual?: 'only' | 'exclude' | 'all';
  /** `'only'` for the recipe picker; omitted keeps materials and products alike. */
  material?: 'only' | 'exclude' | 'all';
};

/** Top-N by relevance. Small on purpose: this feeds a scrollable picker list. */
const PAGE_SIZE = 20;

/**
 * Debounced SERVER-side product search for admin pickers.
 *
 * Every product picker in admin used to fetch one `pageSize: 100`/`200` page of
 * the whole catalog and filter it in the browser, so anything past that page was
 * unreachable — and the five pickers that wrapped it in a search box were worse,
 * because they looked like they worked. `GET /admin/products` has had a real
 * Postgres FTS + trigram `search` param all along; this hook is just the plumbing
 * that finally uses it.
 *
 * An EMPTY term returns the first page (`featured DESC, name ASC`) rather than
 * nothing, so focusing a picker still shows something — for the small tenants the
 * old `<Select>` served fine, requiring a keystroke first would be a regression.
 */
export function useProductSearch(args: {
  term: string;
  filter?: ProductSearchFilter;
  enabled?: boolean;
}): { results: Product[]; isLoading: boolean } {
  const { term, filter, enabled = true } = args;
  const [debounced, setDebounced] = useState('');

  // Inline, like member-picker.tsx — the repo has no useDebounce helper and
  // doesn't want a lodash dependency for one timer.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const q = useQuery({
    queryKey: [
      'product-search',
      debounced,
      filter?.activeOnly ?? null,
      filter?.componentEligible ?? null,
      filter?.componentAllowBundles ?? null,
      filter?.virtual ?? null,
      filter?.material ?? null,
    ],
    queryFn: () =>
      listProducts({
        pageSize: PAGE_SIZE,
        ...(debounced ? { search: debounced } : {}),
        ...filter,
      }),
    enabled,
    staleTime: 30_000,
  });

  return { results: q.data ?? [], isLoading: q.isLoading };
}
